import { useEffect, useState } from 'react';
import { useUser, SignInButton, SignedIn, SignedOut } from '@clerk/clerk-react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { checkUser } from '../service/api/AuthService';
import NoDomainFound from './NoDomainFound';
import './style/Login.css';
import UserNotFound from './UserNotFound';

const Login = () => {
  const { isSignedIn, user } = useUser();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [noDomain, setNoDomain] = useState(false);
  const [userNotFound, setUserNotFound] = useState(false); 
  const [userEmail, setUserEmail] = useState(''); 

  useEffect(() => {
    const authenticate = async () => {
      if (isSignedIn && user) {
        setLoading(true);
        
        try {
          const email = user.primaryEmailAddress.emailAddress;
          setUserEmail(email); // Sauvegarder l'email
          
          const result = await checkUser(email);
          
          //  Utilisateur n'existe PAS (exists: false ou 404)
          if (result.exists === false || !result) {
            setUserNotFound(true); // Afficher le composant UserNotFound
            setLoading(false);
            return;
          }

          //  Utilisateur est Admin
          if (result.isAdmin) {
            sessionStorage.removeItem("selectedDomain");
            toast.success("Bienvenue Administrateur !", { position: "top-center" });
            navigate('/Trade-Dashboard-admin');
            return;
          } 

          // Utilisateur a un domaine sélectionné
          if (result.selectedDomain) {
            sessionStorage.setItem("selectedDomain", JSON.stringify(result.selectedDomain));
            
            if (result.selectedDomain.isValidationManager) {
              toast.success("Bienvenue Validateur !", { position: "top-center" });
              navigate('/Trade-Dashboard-validateur');
            } 
            else if (result.selectedDomain.role === 'manager') {
              toast.success("Bienvenue Manager !", { position: "top-center" });
              navigate('/Trade-Dashboard-manager');
            } else {
              toast.success("Bienvenue Client !", { position: "top-center" });
              navigate('/Trade-Dashboard-client');
            }
            return;
          }

          //  Utilisateur a plusieurs domaines
          if (result.domains && result.domains.length > 1) {
            toast.info("Veuillez sélectionner un domaine avant de continuer.", { position: "top-center" });
            sessionStorage.setItem("domains", JSON.stringify(result.domains));
            navigate('/page/app/select-domain', { state: { domains: result.domains } });
            return;
          }

          // Utilisateur a un seul domaine
          if (result.domains && result.domains.length === 1) {
            const singleDomain = result.domains[0];
            sessionStorage.setItem("selectedDomain", JSON.stringify(singleDomain));
            
            if (singleDomain.isValidationManager) {
              toast.success("Bienvenue Validateur !", { position: "top-center" });
              navigate('/Trade-Dashboard-validateur');
            } 
            else if (singleDomain.role === 'manager') {
              toast.success("Bienvenue Manager !", { position: "top-center" });
              navigate('/Trade-Dashboard-manager');
            } else {
              toast.success("Bienvenue Client !", { position: "top-center" });
              navigate('/Trade-Dashboard-client');
            }
            return;
          }
          
          // ✅ CAS 6 : Aucun domaine trouvé (mais l'utilisateur existe)
          toast.warning("Aucun domaine trouvé pour votre compte. Veuillez contacter un administrateur.", { position: "top-center" });
          setNoDomain(true);

        } catch (error) {
          console.error('Erreur auth:', error);
          setLoading(false); // ✅ Toujours arrêter le loading
          
          // Si l'erreur est un 404 (utilisateur introuvable)
          if (error?.response?.status === 404) {
            setUserNotFound(true);
            return;
          } 
          
          // Autres erreurs
          toast.error(`Erreur de connexion. Veuillez réessayer.`, { position: "top-center" });
        } finally {
          setLoading(false);
        }
      }
    };

    authenticate();
  }, [isSignedIn, user, navigate]);

  // Utilisateur non trouvé (404)
  if (userNotFound) {
    return <UserNotFound userEmail={userEmail} />;
  }

  // Chargement
  if (loading) {
    return (
      <div className="loading-container">
        <div className="loading-spinner">
          <div className="spinner"></div>
          <p className='text text-white-50'>Vérification de vos accès...</p>
        </div>
      </div>
    );
  }

  //  Aucun domaine trouvé
  if (noDomain) {
    return <NoDomainFound />;
  }

  // Page de connexion
  return (
    <>
      {/* Afficher la page de connexion uniquement si NON connecté */}
      <SignedOut>
        <div className="login-container">
          <div className="login-background">
            {/* Effet de dégradé animé */}
            <div className="gradient-bg">
              <div className="gradient-1"></div>
              <div className="gradient-2"></div>
              <div className="gradient-3"></div>
            </div>
          </div>

          <div className="login-content">
            {/* Carte de connexion */}
            <div className="login-card">
              {/* Logo */}
              <div className="logo-container">
                <img
                  className="logo-abbr"
                  src="https://cdn.prod.website-files.com/68063d6193c1f009cf1fdccd/6806b7b5df7ca03c524c1008_Projet%20Studio%20(1).png"
                  alt="TradeVault Logo"
                />
              </div>

              {/* Titre et description */}
              <div className="welcome-section">
                <h1 className="welcome-title">Bienvenue sur TradeVault</h1>
                <p className="welcome-subtitle">
                  Plateforme de trading sécurisée pour la gestion de vos investissements
                </p>
              </div>

              {/* Bouton de connexion avec le nouveau SignInButton */}
              <div className="signin-section">
                <SignInButton mode="modal">
                  <button className="custom-signin-btn">
                    <span className="btn-icon">
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M12 12C14.2091 12 16 10.2091 16 8C16 5.79086 14.2091 4 12 4C9.79086 4 8 5.79086 8 8C8 10.2091 9.79086 12 12 12Z" fill="currentColor"/>
                        <path d="M12 14C9.33 14 4 15.34 4 18V20H20V18C20 15.34 14.67 14 12 14Z" fill="currentColor"/>
                      </svg>
                    </span>
                    <span className="btn-text">Se connecter</span>
                    <span className="btn-arrow">→</span>
                  </button>
                </SignInButton>

                {/* Informations de sécurité */}
                <div className="security-info">
                  <div className="security-badge">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M12 1L3 5V11C3 16.55 6.84 21.74 12 23C17.16 21.74 21 16.55 21 11V5L12 1ZM12 11H19C18.47 15.85 15.5 20.1 12 21.32C8.5 20.1 5.53 15.85 5 11H12Z"/>
                    </svg>
                    <span>Authentification sécurisée</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className="login-footer">
              <p>&copy; 2025 TradeVault. Tous droits réservés.</p>
              <div className="footer-links">
                <a href="#privacy">Confidentialité</a>
                <a href="#terms">Conditions d'utilisation</a>
                <a href="#support">Support</a>
              </div>
            </div>
          </div>
        </div>
      </SignedOut>

      {/* Si connecté, afficher le spinner pendant la redirection */}
      <SignedIn>
        <div className="loading-container">
          <div className="loading-spinner">
            <div className="spinner"></div>
            <p className='text text-white-50'>Redirection en cours...</p>
          </div>
        </div>
      </SignedIn>
    </>
  );
};

export default Login;